import React, { Component, type ReactNode } from 'react';

import { TextEditorContainer } from 'src/app/views/components/TextEditorContainer';

// import our custom global styles for ck editor html elements
import './index.css';

interface Props {
  id?: string;
  characterLimit?: number;
  helpText?: ReactNode;
  message?: string | null;
  onBlur?: () => void;
  onChange?: (data: any) => void;
  onFocus?: () => void;
  value?: string;
  valid?: boolean;
  enableHyperlinks?: boolean;
}

interface State {
  CkEditorModule?: any;
  ClassicEditorModule?: any;
  contentHtml?: string;
}

export class RichTextEditor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      CkEditorModule: null,
      ClassicEditorModule: null,
      contentHtml: props.value,
    };
  }

  componentDidMount() {
    import(
      // @ts-ignore
      /* webpackChunkName: "wysiwyg-react" */ '@ckeditor/ckeditor5-react'
    ).then((module) => {
      this.setState({
        CkEditorModule: module.CKEditor,
      });
    });

    import(
      // @ts-ignore
      /* webpackChunkName: "wysiwyg-classic" */ '@ckeditor/ckeditor5-build-classic'
    ).then((module) => {
      this.setState({
        ClassicEditorModule: module.default,
      });
    });
  }

  render() {
    const { CkEditorModule, ClassicEditorModule, contentHtml } = this.state;
    const { characterLimit, helpText, id, message, valid, enableHyperlinks } =
      this.props;

    if (!CkEditorModule || !ClassicEditorModule) {
      return null;
    }

    const toolbar = [
      'heading',
      'bold',
      'italic',
      'bulletedList',
      'numberedList',
    ];

    const removedPlugins = [
      'CKFinder', // Disable copy/pasting images
    ];

    const editorProps = {
      editor: ClassicEditorModule,
      config: {
        link: {
          addTargetToExternalLinks: true,
          defaultProtocol: 'https://',
        },
        toolbar: enableHyperlinks ? [...toolbar, 'link'] : toolbar,
        removePlugins: enableHyperlinks
          ? [removedPlugins]
          : [...removedPlugins, 'AutoLink', 'Link'],
        heading: {
          options: [
            {
              model: 'paragraph',
              title: 'Paragraph',
              class: 'ck-heading_paragraph',
            },
            {
              model: 'heading2',
              view: 'h2',
              title: 'Heading',
              class: 'ck-heading_heading2',
            },
          ],
        },
      },
      data: contentHtml,
      onChange: (_event: any, editor: any) => {
        const data = editor.getData();
        this.setState({
          contentHtml: data,
        });

        this.props.onChange?.(data);
      },
      onBlur: this.props.onBlur,
      onFocus: this.props.onFocus,
    };

    const containerProps = {
      characterLimit,
      helpText,
      id,
      message,
      sanitize: false,
      valid,
      value: contentHtml,
    };

    return (
      <TextEditorContainer {...containerProps}>
        <CkEditorModule {...editorProps} />
      </TextEditorContainer>
    );
  }
}
